/**
 * ValidationFeedback Utility
 * 
 * Provides real-time validation feedback system for JSON input fields
 * Works with JSONValidator to provide immediate user feedback
 */

import { JSONValidator } from '../services/JSONValidator.js'

export class ValidationFeedback {
    constructor(inputElement, feedbackElement, options = {}) {
        this.inputElement = inputElement
        this.feedbackElement = feedbackElement
        this.options = {
            debounceDelay: 300,
            showSuggestions: true,
            showLineNumbers: false,
            ...options
        }
        
        this.currentCleanup = null
        this.lastValidationResult = null
        this.characterCount = 0
        
        this.init()
    }

    /**
     * Initialize the validation feedback system
     */
    init() {
        if (!this.inputElement || !this.feedbackElement) {
            console.error('ValidationFeedback: Input or feedback element not found')
            return
        }

        // Set up event listeners
        this.inputElement.addEventListener('input', this.handleInput.bind(this))
        this.inputElement.addEventListener('paste', this.handlePaste.bind(this))
        
        // Initial validation if there's existing content
        if (this.inputElement.value.trim()) {
            this.characterCount = this.inputElement.value.length
            this.validateInput(this.inputElement.value)
        }
    }

    /**
     * Handle input events with debounced validation
     */
    handleInput(event) {
        const value = event.target.value
        this.characterCount = value.length
        this.validateInput(value)
    }

    /**
     * Handle paste events with immediate validation
     */
    handlePaste(event) {
        // Use setTimeout to get the pasted content after it's inserted
        setTimeout(() => {
            this.characterCount = this.inputElement.value.length
            this.validateInput(this.inputElement.value)
        }, 10)
    }

    /**
     * Validate input with debouncing
     */
    validateInput(value) {
        // Cancel previous validation if still pending
        if (this.currentCleanup) {
            this.currentCleanup()
        }

        // Set up new debounced validation
        this.currentCleanup = JSONValidator.validateRealTime(
            value,
            this.handleValidationResult.bind(this),
            this.options.debounceDelay
        )
    }

    /**
     * Handle validation results and update feedback display
     */
    handleValidationResult(result) {
        this.lastValidationResult = result
        this.updateFeedbackDisplay(result)
        
        // Trigger custom event for external listeners
        const event = new CustomEvent('validationUpdate', {
            detail: { result, isValid: result.isValid }
        })
        this.inputElement.dispatchEvent(event)
    }

    /**
     * Update the feedback display based on validation result
     */
    updateFeedbackDisplay(result) {
        if (!this.feedbackElement) return

        // Clear previous feedback
        this.feedbackElement.innerHTML = ''
        this.feedbackElement.className = 'validation-feedback'

        if (result.isValid) {
            this.showSuccessFeedback(result)
        } else {
            this.showErrorFeedback(result)
        }
    }

    /**
     * Show success feedback for valid JSON
     */
    showSuccessFeedback(result) {
        this.feedbackElement.classList.add('valid')
        
        const successMessage = document.createElement('div')
        successMessage.className = 'success-message'
        
        // Format as single line with type info in parentheses
        let message = 'Valid JSON'
        if (result.data !== null && result.data !== undefined) {
            const typeInfo = this.getDataTypeInfo(result.data)
            message += ` (${typeInfo})`
        }
        
        // Add character count
        const charCountText = `${this.characterCount} character${this.characterCount !== 1 ? 's' : ''}`
        
        successMessage.innerHTML = `
            <div class="status-content">
                <span class="icon">✓</span>
                <span class="message">${message}</span>
            </div>
            <span class="char-count">${charCountText}</span>
        `
        
        this.feedbackElement.appendChild(successMessage)
    }

    /**
     * Show error feedback for invalid JSON
     */
    showErrorFeedback(result) {
        this.feedbackElement.classList.add('invalid')
        
        const errorMessage = document.createElement('div')
        errorMessage.className = 'error-message'
        
        // Add character count for errors too
        const charCountText = `${this.characterCount} character${this.characterCount !== 1 ? 's' : ''}`
        
        errorMessage.innerHTML = `
            <div class="status-content">
                <span class="icon">✗</span>
                <span class="message">${result.message}</span>
            </div>
            <span class="char-count">${charCountText}</span>
        `
        
        this.feedbackElement.appendChild(errorMessage)

        // Show suggestions if enabled
        if (this.options.showSuggestions && result.suggestions) {
            this.showSuggestions(result.suggestions)
        }
    }

    /**
     * Show suggestions for fixing JSON errors
     */
    showSuggestions(suggestions) {
        if (!suggestions || suggestions.length === 0) return

        const suggestionsContainer = document.createElement('div')
        suggestionsContainer.className = 'suggestions'
        
        const suggestionsTitle = document.createElement('div')
        suggestionsTitle.className = 'suggestions-title'
        suggestionsTitle.textContent = 'Suggestions:'
        suggestionsContainer.appendChild(suggestionsTitle)

        const suggestionsList = document.createElement('ul')
        suggestionsList.className = 'suggestions-list'
        
        suggestions.forEach(suggestion => {
            const listItem = document.createElement('li')
            listItem.textContent = suggestion
            suggestionsList.appendChild(listItem)
        })
        
        suggestionsContainer.appendChild(suggestionsList)
        this.feedbackElement.appendChild(suggestionsContainer)
    }

    /**
     * Get human-readable data type information
     */
    getDataTypeInfo(data) {
        if (Array.isArray(data)) {
            return `Array with ${data.length} element${data.length !== 1 ? 's' : ''}`
        } else if (typeof data === 'object' && data !== null) {
            const keys = Object.keys(data)
            return `Object with ${keys.length} propert${keys.length !== 1 ? 'ies' : 'y'}`
        } else if (typeof data === 'string') {
            return `String (${data.length} character${data.length !== 1 ? 's' : ''})`
        } else if (typeof data === 'number') {
            return `Number (${data})`
        } else if (typeof data === 'boolean') {
            return `Boolean (${data})`
        } else if (data === null) {
            return 'Null value'
        }
        
        return `${typeof data}`
    }

    /**
     * Get the last validation result
     */
    getLastValidationResult() {
        return this.lastValidationResult
    }

    /**
     * Manually trigger validation
     */
    validate() {
        if (this.inputElement) {
            this.characterCount = this.inputElement.value.length
            this.validateInput(this.inputElement.value)
        }
    }

    /**
     * Update character count externally
     */
    updateCharacterCount(count) {
        this.characterCount = count
        // If we have a last validation result, update the display
        if (this.lastValidationResult) {
            this.updateFeedbackDisplay(this.lastValidationResult)
        }
    }

    /**
     * Clear feedback display
     */
    clearFeedback() {
        if (this.feedbackElement) {
            this.feedbackElement.innerHTML = ''
            this.feedbackElement.className = 'validation-feedback'
        }
        this.lastValidationResult = null
    }

    /**
     * Destroy the validation feedback system
     */
    destroy() {
        // Cancel pending validation
        if (this.currentCleanup) {
            this.currentCleanup()
        }

        // Remove event listeners
        if (this.inputElement) {
            this.inputElement.removeEventListener('input', this.handleInput.bind(this))
            this.inputElement.removeEventListener('paste', this.handlePaste.bind(this))
        }

        // Clear references
        this.inputElement = null
        this.feedbackElement = null
        this.currentCleanup = null
        this.lastValidationResult = null
    }
}